import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'

export type BrandDialogVariant = 'info' | 'success' | 'error' | 'confirm' | 'danger'

interface BrandDialogProps {
  open: boolean
  title: string
  message: string
  variant?: BrandDialogVariant
  confirmLabel?: string
  cancelLabel?: string
  showCancel?: boolean
  onConfirm: () => void
  onCancel: () => void
}

const BrandDialog: React.FC<BrandDialogProps> = ({
  open,
  title,
  message,
  variant = 'info',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  showCancel = false,
  onConfirm,
  onCancel,
}) => {
  const confirmClass =
    variant === 'danger' || variant === 'error'
      ? 'inline-flex items-center justify-center rounded-full bg-ink text-white text-sm font-semibold px-6 py-2.5 hover:bg-ink-soft transition-colors'
      : 'btn-gold text-sm px-6 py-2.5'

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onCancel])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <motion.button
            type="button"
            aria-label="Close dialog"
            className="absolute inset-0 bg-ink/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
          />

          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="brand-dialog-title"
            aria-describedby="brand-dialog-message"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.22 }}
            className="relative w-full max-w-sm bg-cream rounded-3xl shadow-soft-lg border border-gray-200/80 overflow-hidden"
          >
            <div className="px-7 pt-6 pb-2 flex items-start justify-between">
              <img
                src="/images/logo-dark.webp"
                alt="Fit by Cassie"
                className="h-8 w-auto"
              />
              <button
                type="button"
                onClick={onCancel}
                className="p-1.5 -mr-1 text-gray-400 hover:text-ink transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-7 pt-4 pb-7">
              <div className="divider-gold mb-5" />
              <h2
                id="brand-dialog-title"
                className="font-display text-2xl font-semibold text-ink leading-snug"
              >
                {title}
              </h2>
              <p
                id="brand-dialog-message"
                className="mt-3 text-sm text-gray-600 leading-relaxed"
              >
                {message}
              </p>

              <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                {showCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    className="btn-secondary text-sm px-6 py-2.5"
                  >
                    {cancelLabel}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onConfirm}
                  className={confirmClass}
                  autoFocus
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default BrandDialog
